/* eslint-disable no-console */
/* eslint-disable camelcase */
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import userUtils from 'utils/User';
import p13nUtils from 'utils/localStorage/P13n';
import BannerListBindings from 'analytics/bindingMethods/components/bccComponents/BannerList/BannerListBindings';
import { p13nSelector } from 'selectors/p13n/p13nSelector';
import { sendCmsComponentEvent, matchContexts, getPersonalizationInfo } from 'analytics/utils/cmsComponents';
import { personalizationSelector } from 'viewModel/selectors/personalization/personalizationSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import constants from 'constants/content';
import Empty from 'constants/empty';
import anaConsts from 'analytics/constants';
import PersonalizationUtils from 'utils/Personalization';
import { getOrCreateAbTestUuid } from 'utils/localStorage/AbTestUuid';
import homepageActions from 'actions/HomepageActions';
import { TRENDING_CONTENT } from 'services/api/bannerList/getTrendingContentData';
import cookieUtils from 'utils/Cookies';

const {
    COMPONENT_TYPES: { BANNER_LIST }
} = constants;

const {
    CMS_COMPONENT_EVENTS: { IMPRESSION, ITEM_CLICK }
} = anaConsts;

const { wrapHOC } = FrameworkUtils;

const formatNBCTrackingData = (personalization, akamaiabtestdata) => {
    const { trendingContentAB = Empty.Object, isTrendingEnabled = false } = personalization || Empty.Object;
    const trackingSource = isTrendingEnabled && trendingContentAB.isABTestEnabled ? trendingContentAB : personalization || Empty.Object;
    const { activeAbTestIds = Empty.Array, abTestTraffic = '', abTestEndDate = '', isABTestEnabled = false } = trackingSource;

    // Generate or retrieve AB test-scoped UUID when isTrendingEnabled is true and abTestId is present
    let uuid = null;

    if (isTrendingEnabled && trendingContentAB?.abTestId) {
        uuid = getOrCreateAbTestUuid(trendingContentAB.abTestId, trendingContentAB.abTestEndDate);
    }

    return isTrendingEnabled
        ? `activeAbTestIds:${activeAbTestIds.join(',')};abTestTraffic:${abTestTraffic};abTestEndDate:${abTestEndDate};isABTestEnabled:${isABTestEnabled};deviceIdentifier:${uuid || ''};${akamaiabtestdata}`
        : '';
};

const functions = (dispatch, ownProps) => ({
    triggerImpression: function () {
        const { items } = ownProps;
        const personalizationData = p13nUtils.getAllPersonalizedCache();
        const biStatus = userUtils.getBiStatus();
        const profileId = userUtils.getProfileId();
        const profileStatus = userUtils.getProfileStatus();
        const bannersPersonalizedData = [];
        const userData = {
            biStatus,
            profileId,
            profileStatus
        };

        // Check for each item in the banner list to see if its context matches any of the
        // contexts for personalization data in the local storage.
        items.forEach(item => {
            const { personalization } = item;

            if (personalization) {
                const personalizationLocalData = matchContexts(personalization, personalizationData);

                if (personalizationLocalData) {
                    bannersPersonalizedData.push(personalizationLocalData);
                }
            }
        });

        // Only trigger analytics for viewable impression if they also have personalization data.
        if (bannersPersonalizedData.length > 0) {
            userData.bannersPersonalizedData = bannersPersonalizedData;
            BannerListBindings.bannerItemsImpression(userData);
        }
    },
    triggerCMSImpression: function (targets, renderItems, bannerListProps = {}) {
        const {
            items,
            sid,
            title = '',
            blPlacement = '',
            sys = {},
            personalization: defaultPersonalization = Empty.Object,
            akamaiabtestdata = ''
        } = bannerListProps || ownProps;

        const {
            isNBCEnabled = null,
            bannerPoolId = '',
            isTrendingEnabled = null,
            client_request_id = '',
            timestamp = '',
            context: parentContextId = '',
            guardrails = [],
            NBCSlotCounts = Empty.Object
        } = defaultPersonalization;

        const sourceItems = renderItems || items || [];

        const currentItems = sourceItems
            .map((item, index) => ({
                ...item,
                itemIndex: index,
                isNBCEnabled,
                blPlacement,
                isTrendingEnabled,
                parentContextId,
                timestamp: timestamp,
                clientRequestId: client_request_id,
                bannerPoolId,
                NBCSlotCounts: NBCSlotCounts?.web ?? null
            }))
            .filter((_, index) => targets.includes(index));

        const cmsEventData = {
            items: currentItems,
            eventName: IMPRESSION,
            title,
            sid,
            guardrails,
            publishedVersion: sys?.publishedVersion || '',
            component: BANNER_LIST,
            nbcTrackingData: formatNBCTrackingData(defaultPersonalization, akamaiabtestdata)
        };

        const prvCookieCheck = cookieUtils.read(cookieUtils.KEYS.P13N_PRV);
        const skipForPreview = prvCookieCheck && !userUtils.isAnonymous() && !getPersonalizationInfo().personalizationData.length;

        if (!skipForPreview) {
            setTimeout(() => {
                sendCmsComponentEvent(cmsEventData);
            }, 1000);
        }
    },
    triggerClick: async function (data, position, bannerListProps = {}) {
        const { sid: bannerSid, variationData } = data;

        const {
            items,
            sid,
            title = '',
            blPlacement = '',
            personalization: defaultPersonalization = Empty.Object,
            akamaiabtestdata = ''
        } = bannerListProps || ownProps;

        const {
            isNBCEnabled = null,
            bannerPoolId = '',
            isTrendingEnabled = null,
            client_request_id = '',
            timestamp = '',
            context: parentContextId = '',
            guardrails = [],
            NBCSlotCounts = Empty.Object
        } = defaultPersonalization;

        const sourceItems = variationData ? [variationData] : items || [];

        const currentItems = sourceItems.map((item, index) => ({
            ...item,
            itemIndex: variationData ? position : index,
            isNBCEnabled,
            blPlacement,
            isTrendingEnabled,
            parentContextId,
            timestamp,
            clientRequestId: client_request_id,
            bannerPoolId,
            NBCSlotCounts: NBCSlotCounts?.web ?? null
        }));

        await sendCmsComponentEvent({
            items: currentItems,
            eventName: ITEM_CLICK,
            title,
            sid,
            guardrails,
            clickedSid: bannerSid,
            component: BANNER_LIST,
            nbcTrackingData: formatNBCTrackingData(defaultPersonalization, akamaiabtestdata)
        });
    },
    getTrendingContent: function () {
        const { blPlacement, personalization = Empty.Object } = ownProps;

        if (!TRENDING_CONTENT[blPlacement]) {
            return undefined;
        }

        return dispatch(homepageActions.loadTrendingContent({ blPlacement, personalization }));
    }
});

const connectedBannerList = connect(
    createSelector(
        coreUserDataSelector,
        p13nSelector,
        personalizationSelector,
        (_state, ownProps) => ownProps,
        (user, p13n, personalization, ownProps) => {
            const { isNBCEnabled = false, NBCSlotCounts = Empty.Object, bannerPoolId } = ownProps?.personalization || Empty.Object;
            const { isTrending } = ownProps;
            const { isPersonalizationInitializing } = personalization;
            const personalizedComponent = (isNBCEnabled && personalization.personalizedComponent) || Empty.Object;

            let isTrendingContentEnabled = false;

            // Only enable when the placement maps to a known trending code — otherwise there is
            // nothing to fetch and the banner would sit in a skeleton that never resolves.
            const hasTrendingPlacement = Boolean(TRENDING_CONTENT[ownProps?.blPlacement]);

            if (hasTrendingPlacement && (isTrending || ownProps?.personalization?.isTrendingEnabled)) {
                if (user.isAnonymous) {
                    isTrendingContentEnabled = true;
                } else {
                    if (isNBCEnabled && (Object.keys(personalizedComponent).length === 0 || personalizedComponent?.length === 0)) {
                        isTrendingContentEnabled = true;
                    }
                }
            }

            const showPersonalizationOverlay = PersonalizationUtils.shouldShowPersonalizationOverlay(isNBCEnabled);
            const nonTargettedVariations = showPersonalizationOverlay ? ownProps.items : undefined;
            const _items = [...ownProps.items];

            let itemsToShow = ownProps.items;

            // Limit the number of items to display given the slots count for signed in users if enabled for NBC
            if (!user.isAnonymous && isNBCEnabled && NBCSlotCounts?.web) {
                itemsToShow = _items.slice(0, NBCSlotCounts.web);
            }

            const trendingForPlacement = p13n.trendingContent?.[TRENDING_CONTENT[ownProps.blPlacement]];
            const { variationData: trendingContent = null, akamaiabtestdata = null } = trendingForPlacement || Empty.Object;

            return {
                p13n,
                personalizedComponent,
                bannerPoolId: isNBCEnabled ? bannerPoolId : null,
                isPersonalizationInitializing,
                nonTargettedVariations,
                items: itemsToShow,
                isTrendingContentEnabled,
                isTrendingContentPending: Boolean(trendingForPlacement?.pending),
                isTrendingContentLoaded: Boolean(trendingForPlacement) && !trendingForPlacement.pending,
                trendingContent,
                akamaiabtestdata
            };
        }
    ),
    functions
);

const withBannerListProps = wrapHOC(connectedBannerList);

export {
    functions, withBannerListProps
};
